/**
 * VisualsModal.tsx: Interactive chart visualization modal for NSR Lens chat responses.
 * Part of the NSR Lens chat module — Answer component.
 * Renders bar, line, and stacked bar charts from data_preview payloads using MUI X Charts.
 * Responsibilities: chart type selection, dimension/measure auto-detection, column picker,
 *   top-N limiting, sort control, chart rendering.
 */

import { useMemo, useState } from 'react'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Checkbox from '@mui/material/Checkbox'
import ListItemText from '@mui/material/ListItemText'
import OutlinedInput from '@mui/material/OutlinedInput'
import TextField from '@mui/material/TextField'
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded'
import StackedBarChartRoundedIcon from '@mui/icons-material/StackedBarChartRounded'
import SortByAlphaRoundedIcon from '@mui/icons-material/SortByAlphaRounded'
import SortRoundedIcon from '@mui/icons-material/SortRounded'
import { BarChart } from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'
import { DataPreviewTab } from '../../api'

import styles from './VisualsModal.module.css'

/** Fraction of rows that must be numeric for a column to classify as a measure */
const NUMERIC_THRESHOLD = 0.9

/** Default number of data points to display */
const DEFAULT_TOP_N = 10

/**
 * Column name substrings that force a column to be classified as a dimension
 * even when its values are numeric (e.g. "Year Code", "Month Code").
 */
const DIMENSION_FORCE_KEYWORDS = ['code', 'cd', 'year', 'month', 'week', 'day', 'quarter']

/** Chart type options */
type ChartType = 'bar' | 'line' | 'stacked'

/** Sort direction options */
type SortOption = 'none' | 'dim-asc' | 'dim-desc' | 'measure-asc' | 'measure-desc'

/**
 * Props for the VisualsModal component.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is visible.
 * @param {() => void} props.onClose - Callback invoked when the modal should close.
 * @param {DataPreviewTab[]} props.dataTabs - Array of data preview tabs from the chat response.
 */
interface VisualsModalProps {
  open: boolean
  onClose: () => void
  dataTabs: DataPreviewTab[]
}

/**
 * Attempts to parse a value as a finite number.
 * Handles comma-formatted integers and plain numbers.
 *
 * @param {unknown} value - The value to parse.
 * @returns {number | null} The parsed number or null if not numeric.
 */
const parseNumeric = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string') return null
  const text = value.trim()
  if (!text) return null
  // Strip commas from formatted numbers before parsing
  const cleaned = text.replace(/,/g, '')
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

/**
 * Classifies columns in a row dataset into dimensions (categorical) and measures (numeric).
 * A column is a measure if at least NUMERIC_THRESHOLD of its non-empty values are numeric
 * AND its name does not contain any dimension-force keyword (e.g. "code", "year").
 *
 * @param {Record<string, any>[]} rows - Array of row objects.
 * @returns {{ dimensions: string[]; measures: string[] }} Classified column names.
 */
const classifyColumns = (rows: Record<string, any>[]): { dimensions: string[]; measures: string[] } => {
  if (!rows.length) return { dimensions: [], measures: [] }
  const keys = Object.keys(rows[0])
  const dimensions: string[] = []
  const measures: string[] = []

  for (const key of keys) {
    const lowerKey = key.toLowerCase()

    // Force columns whose name contains code/year/month/etc. into dimensions
    if (DIMENSION_FORCE_KEYWORDS.some(kw => lowerKey.includes(kw))) {
      dimensions.push(key)
      continue
    }

    const nonEmpty = rows.map(r => r[key]).filter(v => v !== null && v !== undefined && v !== '')
    if (nonEmpty.length === 0) {
      dimensions.push(key)
      continue
    }
    const numericCount = nonEmpty.filter(v => parseNumeric(v) !== null).length
    const ratio = numericCount / nonEmpty.length
    if (ratio >= NUMERIC_THRESHOLD) {
      measures.push(key)
    } else {
      dimensions.push(key)
    }
  }
  return { dimensions, measures }
}

/**
 * Formats a number for the y-axis tick labels using compact notation
 * so large values are not clipped (e.g. 1,200,000 → "1.2M").
 *
 * @param {number} value - The axis tick value.
 * @returns {string} A compact formatted string.
 */
const formatAxisValue = (value: number): string => {
  const abs = Math.abs(value)
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return value.toLocaleString('en-US', { maximumFractionDigits: 1 })
}

/* Shared axis style: medium gray for lines and ticks — darker than chart fills but not black */
const AXIS_STYLE = {
  tickLabelStyle: { fontSize: 11, fill: '#757575' },
  labelStyle: { fontSize: 12, fill: '#616161' },
  line: { stroke: '#9e9e9e' },
  tick: { stroke: '#9e9e9e' },
} as const

/**
 * Renders an interactive chart modal with chart type selection, dimension/measure pickers,
 * top-N limiter, sort controls, and MUI X Charts rendering.
 * Opens from the Answer card "Visuals" button.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is visible.
 * @param {() => void} props.onClose - Callback to close the modal.
 * @param {DataPreviewTab[]} props.dataTabs - Data preview tabs from the response payload.
 * @returns {JSX.Element} The rendered modal.
 */
export const VisualsModal = ({ open, onClose, dataTabs }: VisualsModalProps) => {
  // Track active tab, chart type, selected columns, top-N, and sort
  const [activeTab, setActiveTab] = useState(0)
  const [chartType, setChartType] = useState<ChartType>('bar')
  const [selectedDimension, setSelectedDimension] = useState<string>('')
  const [selectedMeasures, setSelectedMeasures] = useState<string[]>([])
  const [topN, setTopN] = useState<number>(DEFAULT_TOP_N)
  const [sortOption, setSortOption] = useState<SortOption>('none')

  const tabData = dataTabs[activeTab]
  const rows: Record<string, any>[] = tabData?.preview ?? []

  // Classify columns into dimensions and measures whenever tab data changes
  const { dimensions, measures } = useMemo(() => classifyColumns(rows), [rows])

  // Auto-select first dimension and first measure when classification changes
  const activeDimension = selectedDimension && dimensions.includes(selectedDimension)
    ? selectedDimension
    : dimensions[0] ?? ''

  const activeMeasures = selectedMeasures.length > 0 && selectedMeasures.some(m => measures.includes(m))
    ? selectedMeasures.filter(m => measures.includes(m))
    : measures.length > 0 ? [measures[0]] : []

  /**
   * Handles chart type toggle change.
   *
   * @param {React.MouseEvent<HTMLElement>} _event - The click event.
   * @param {ChartType | null} newType - The newly selected chart type.
   */
  const handleChartTypeChange = (_event: React.MouseEvent<HTMLElement>, newType: ChartType | null) => {
    if (newType !== null) setChartType(newType)
  }

  /**
   * Handles dimension selector change.
   *
   * @param {SelectChangeEvent<string>} event - The select change event.
   */
  const handleDimensionChange = (event: SelectChangeEvent<string>) => {
    setSelectedDimension(event.target.value)
  }

  /**
   * Handles measure multi-select change.
   *
   * @param {SelectChangeEvent<string[]>} event - The select change event.
   */
  const handleMeasureChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value
    setSelectedMeasures(typeof value === 'string' ? value.split(',') : value)
  }

  /**
   * Handles tab change when multiple data preview tabs exist.
   *
   * @param {number} index - The index of the newly selected tab.
   */
  const handleTabChange = (index: number) => {
    setActiveTab(index)
    setSelectedDimension('')
    setSelectedMeasures([])
  }

  /**
   * Handles Top N input change. Clamps to minimum 1.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event.
   */
  const handleTopNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseInt(e.target.value, 10)
    setTopN(Number.isFinite(parsed) && parsed >= 1 ? parsed : DEFAULT_TOP_N)
  }

  // Build chart dataset: map rows, apply sorting, then limit to top-N
  const chartDataset = useMemo(() => {
    if (!activeDimension || activeMeasures.length === 0) return []

    let data = rows.map(row => {
      const entry: Record<string, any> = { [activeDimension]: String(row[activeDimension] ?? '') }
      for (const m of activeMeasures) {
        entry[m] = parseNumeric(row[m]) ?? 0
      }
      return entry
    })

    // Apply sorting based on the selected sort option
    if (sortOption === 'dim-asc') {
      data.sort((a, b) => String(a[activeDimension]).localeCompare(String(b[activeDimension])))
    } else if (sortOption === 'dim-desc') {
      data.sort((a, b) => String(b[activeDimension]).localeCompare(String(a[activeDimension])))
    } else if (sortOption === 'measure-asc') {
      // Sort by the first active measure ascending
      const sortKey = activeMeasures[0]
      data.sort((a, b) => (a[sortKey] as number) - (b[sortKey] as number))
    } else if (sortOption === 'measure-desc') {
      const sortKey = activeMeasures[0]
      data.sort((a, b) => (b[sortKey] as number) - (a[sortKey] as number))
    }

    // Limit to top-N data points for cleaner display
    return data.slice(0, topN)
  }, [rows, activeDimension, activeMeasures, sortOption, topN])

  // Build MUI X Charts series descriptors from the selected measures
  const series = useMemo(
    () => activeMeasures.map(m => ({
      dataKey: m,
      label: m,
      ...(chartType === 'stacked' ? { stack: 'total' } : {})
    })),
    [activeMeasures, chartType]
  )

  // Edge case: no data at all
  if (!rows.length) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box className={styles.visualsModal}>
          <div className={styles.visualsModalHeader}>
            <span className={styles.visualsModalTitle}>Visuals</span>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={styles.chartBody}>
            <span className={styles.emptyMessage}>No data available to chart.</span>
          </div>
        </Box>
      </Modal>
    )
  }

  // Edge case: no numeric columns detected
  if (measures.length === 0) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box className={styles.visualsModal}>
          <div className={styles.visualsModalHeader}>
            <span className={styles.visualsModalTitle}>Visuals</span>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={styles.chartBody}>
            <span className={styles.emptyMessage}>No numeric columns found in the data. Charts require at least one numeric measure.</span>
          </div>
        </Box>
      </Modal>
    )
  }

  const isLineChart = chartType === 'line'

  // Use a soft muted green for single-measure charts; multi-measure gets the full color palette
  const SINGLE_MEASURE_COLOR = '#81c784'
  const MULTI_COLORS = ['#115ea3', '#e5813e', '#2e7d32', '#6b21a8', '#92400e', '#1e40af', '#f40009']
  const chartColors = activeMeasures.length === 1 ? [SINGLE_MEASURE_COLOR] : MULTI_COLORS

  // Equal width for both dropdowns
  const DROPDOWN_WIDTH = 180

  // Shared x-axis config with label rotation for readability
  const xAxisConfig = [{
    scaleType: 'band' as const,
    dataKey: activeDimension,
    ...AXIS_STYLE,
    tickLabelStyle: { ...AXIS_STYLE.tickLabelStyle, angle: -30, textAnchor: 'end' as const },
  }]

  // Y-axis config with compact number formatting to prevent clipping
  const yAxisConfig = [{
    ...AXIS_STYLE,
    valueFormatter: formatAxisValue,
  }]

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.visualsModal}>
        {/* Header */}
        <div className={styles.visualsModalHeader}>
          <span className={styles.visualsModalTitle}>Visuals</span>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Tab bar for multiple data sources */}
        {dataTabs.length > 1 && (
          <div className={styles.controlsBar} style={{ borderBottom: '0.5px solid #e0e0e0', paddingBottom: 0 }}>
            {dataTabs.map((tab, idx) => (
              <button
                key={idx}
                onClick={() => handleTabChange(idx)}
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  borderBottom: idx === activeTab ? '2px solid #f40009' : '2px solid transparent',
                  background: idx === activeTab ? '#fef1f1' : 'transparent',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#323130'
                }}
              >
                {tab.description}
              </button>
            ))}
          </div>
        )}

        {/* Controls bar: chart type, dimension, measure, top-N, sort */}
        <div className={styles.controlsBar}>
          {/* Chart type toggle */}
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={handleChartTypeChange}
            size="small"
            aria-label="Chart type selector"
          >
            <ToggleButton value="bar" aria-label="Bar chart" sx={{ textTransform: 'none', fontSize: 12, gap: 0.5, color: '#616161' }}>
              <BarChartRoundedIcon sx={{ fontSize: 16 }} /> Bar
            </ToggleButton>
            <ToggleButton value="line" aria-label="Line chart" sx={{ textTransform: 'none', fontSize: 12, gap: 0.5, color: '#616161' }}>
              <ShowChartRoundedIcon sx={{ fontSize: 16 }} /> Line
            </ToggleButton>
            <ToggleButton value="stacked" aria-label="Stacked bar chart" sx={{ textTransform: 'none', fontSize: 12, gap: 0.5, color: '#616161' }}>
              <StackedBarChartRoundedIcon sx={{ fontSize: 16 }} /> Stacked
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Dimension selector — fixed width matching Measures */}
          <FormControl size="small" sx={{ width: DROPDOWN_WIDTH }}>
            <InputLabel id="visuals-dim-label" sx={{ fontSize: 13 }}>Dimension</InputLabel>
            <Select
              labelId="visuals-dim-label"
              value={activeDimension}
              onChange={handleDimensionChange}
              label="Dimension"
              sx={{ fontSize: 13 }}
            >
              {dimensions.map(d => (
                <MenuItem key={d} value={d} sx={{ fontSize: 13 }}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Measure multi-select — same width as Dimension */}
          <FormControl size="small" sx={{ width: DROPDOWN_WIDTH }}>
            <InputLabel id="visuals-meas-label" sx={{ fontSize: 13 }}>Measures</InputLabel>
            <Select
              labelId="visuals-meas-label"
              multiple
              value={activeMeasures}
              onChange={handleMeasureChange}
              input={<OutlinedInput label="Measures" />}
              renderValue={(selected) => (selected as string[]).join(', ')}
              sx={{ fontSize: 13 }}
            >
              {measures.map(m => (
                <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>
                  <Checkbox checked={activeMeasures.indexOf(m) > -1} size="small" />
                  <ListItemText primary={m} primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Top N input — limits rendered data points */}
          <TextField
            size="small"
            type="number"
            label="Top"
            value={topN}
            onChange={handleTopNChange}
            inputProps={{ min: 1 }}
            sx={{ width: 80, '& .MuiInputBase-input': { fontSize: 13 }, '& .MuiInputLabel-root': { fontSize: 13 } }}
          />

          {/* Sort selector with icons */}
          <FormControl size="small" sx={{ width: 170 }}>
            <InputLabel id="visuals-sort-label" sx={{ fontSize: 13 }}>Sort</InputLabel>
            <Select
              labelId="visuals-sort-label"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as SortOption)}
              label="Sort"
              sx={{ fontSize: 13 }}
            >
              <MenuItem value="none" sx={{ fontSize: 13 }}>Default</MenuItem>
              <MenuItem value="dim-asc" sx={{ fontSize: 13 }}>
                <SortByAlphaRoundedIcon sx={{ fontSize: 15, mr: 0.5, color: '#757575' }} /> Dimension A→Z
              </MenuItem>
              <MenuItem value="dim-desc" sx={{ fontSize: 13 }}>
                <SortByAlphaRoundedIcon sx={{ fontSize: 15, mr: 0.5, color: '#757575', transform: 'scaleY(-1)' }} /> Dimension Z→A
              </MenuItem>
              <MenuItem value="measure-asc" sx={{ fontSize: 13 }}>
                <SortRoundedIcon sx={{ fontSize: 15, mr: 0.5, color: '#757575' }} /> Measure ↑
              </MenuItem>
              <MenuItem value="measure-desc" sx={{ fontSize: 13 }}>
                <SortRoundedIcon sx={{ fontSize: 15, mr: 0.5, color: '#757575', transform: 'scaleY(-1)' }} /> Measure ↓
              </MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Chart rendering area */}
        <div className={styles.chartBody}>
          {chartDataset.length === 0 ? (
            <span className={styles.emptyMessage}>Select a dimension and at least one measure to render a chart.</span>
          ) : isLineChart ? (
            <LineChart
              dataset={chartDataset}
              xAxis={xAxisConfig}
              yAxis={yAxisConfig}
              series={series}
              colors={chartColors}
              height={400}
              margin={{ left: 70, bottom: 60 }}
              slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'middle' } } }}
            />
          ) : (
            <BarChart
              dataset={chartDataset}
              xAxis={xAxisConfig}
              yAxis={yAxisConfig}
              series={series}
              colors={chartColors}
              height={400}
              margin={{ left: 70, bottom: 60 }}
              slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'middle' } } }}
            />
          )}
        </div>
      </Box>
    </Modal>
  )
}
